import { Component, forwardRef, Inject, Input, NgZone, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import * as _ from 'lodash';
import { take } from 'rxjs';
import { ErrorMessages, Permissions } from '../../common/constants';
import { DataImport, DataImportState, Thing } from '../../model/index';
import { AuthenticationService } from '../../service/authentication.service';
import { ContextService } from '../../service/context.service';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { CustomTablePage } from '../../shared/class/custom-table-page.class';
import { ButtonActionValue, CustomTableService } from '../../shared/custom-table';
import { DownloadProgressBarDialogComponent } from '../../shared/download-progress-bar-dialog/download-progress-bar-dialog';
import { DatetimeFormatterPipe, DurationFormatterPipe, LocalizationPipe } from '../../shared/pipe';
import { ErrorUtility } from '../../utility/error-utility';
import { DataImportAddDialogComponent } from './data-import-add-dialog.component';
import { DataImportWidgetService } from './data-import-widget.service';

@Component({
    selector: 'data-import-widget',
    template: require('./data-import-widget.component.html'),
    styles: [require('./data-import-widget.component.css')],
    providers: [DataImportWidgetService]
})
export class DataImportWidgetComponent extends CustomTablePage<DataImport> implements OnInit, OnDestroy {

    @Input() title: string;

    @Input() description: string;

    @Input() reconcileConnectionStatus: boolean;

    error: string;
    loaded: boolean;
    writePermission: boolean;
    importData: DataImport[] = [];
    enabled: boolean;
    addButtonDisabled: boolean;

    // filler loading properties
    fillerRowCount: number = 10;
    fillerRowHeight: number = 10;
    fillerRowHeightWithSpace: number = 12;
    fillerRowOffset: number = 10;

    private refreshIntervalMillis = 10000;
    private intervalId: any;
    private timezone: string;
    private language: string;
    private currentThing: Thing;

    constructor(
        @Inject(forwardRef(() => DataImportWidgetService)) private dataImportWidgetService: DataImportWidgetService,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => DatetimeFormatterPipe)) private datetimeFormatterPipe: DatetimeFormatterPipe,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => ViewContainerRef)) private vcRef: ViewContainerRef,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => DurationFormatterPipe)) private durationFormatterPipe: DurationFormatterPipe,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService
    ) {
        super();
    }

    ngOnInit(): void {
        this.sort = ['creationTimestamp', 'desc'];
        this.currentThing = this.thingContextService.getCurrentThing();
        this.enabled = !!this.currentThing;
        this.writePermission = this.authenticationService.hasPermission(Permissions.IMPORT_DATA);
        this.timezone = this.contextService.getTimezone();
        this.language = this.authenticationService.getLocale();
        if (this.enabled) {
            this.computeAddButtonDisabled();
            this.setDisplayedColumns();
            this.loadData();
            this.startRefreshing();
        }
    }

    private computeAddButtonDisabled(): void {
        this.dataImportWidgetService.getPagedDataImports(['creationTimestamp', 'desc'], 0, 1, null).then(pagedList => {
            this.addButtonDisabled = pagedList.content?.some(dataImport => dataImport.state == DataImportState.PENDING);
        }).catch(err => {
            this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
        });
    }

    ngOnDestroy(): void {
        this.stopRefreshing();
    }

    private startRefreshing(): void {
        this.stopRefreshing();
        this.zone.runOutsideAngular(() => {
            this.intervalId = setInterval(() => {
                this.zone.run(() => {
                    this.loadData();
                    this.computeAddButtonDisabled();
                })
            }, this.refreshIntervalMillis)
        });
    }

    private stopRefreshing(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    loadData(): Promise<void> {
        return this.dataImportWidgetService.getPagedDataImports(this.sort, this.pageIndex, this.pageSize, this.searchText).then(pagedList => {
            if (!_.isEqual(this.importData, pagedList.content)) {
                this.importData = pagedList.content;
                this.enrichContent(pagedList.content);
                this.length = pagedList.totalElements;
                this.pageIndex = pagedList.number;
                this.pageSize = pagedList.size;
                this.dataSource = new MatTableDataSource<DataImport>(pagedList.content);
            }
            this.loaded = true;
            this.error = null;
        }).catch(err => {
            this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
        });
    }

    protected setDisplayedColumns(): void {
        let iconValueMapUnrecognizedColumns = {
            "_default": {
                isFontAwesome: false,
                customIconClass: 'material-symbols-outlined filled-icon text-warning',
                customIconHtml: 'release_alert',
                tooltipPath: 'unrecognizedColumnsTooltip',
                tooltipPosition: 'below'
            }
        }
        this.displayedColumns = [
            CustomTableService.newSimpleColumn('dateTime', 'creationDateProperty', 'printedCreationDate').withStyle({ '_any': { 'width': '20%' } }),
            CustomTableService.newSimpleColumn('name', 'nameProperty', 'name').withStyle({ '_any': { 'width': '30%' } }),
            CustomTableService.newPipedColumn('state', 'statusProperty', 'printedState', 'dataImportState'),
            CustomTableService.newIconColumn('unrecognizedColumns', '', 'showUnrecognizedColumns', iconValueMapUnrecognizedColumns, true).withVisiblePath('showUnrecognizedColumns').withStyle({ '_any': { 'padding-top': '4px', 'width': '1%' } }),
            CustomTableService.newButtonColumn('downloadDataImport', '', 'id', 'float-right', 'downloadButton').withMatIcon('download').withMatIconClass('material-symbols-outlined').withVisiblePath('showDownload').withStyle({ '_any': { 'font-size': '20px', 'width': '10px' } }).withStickyEndColumn().withColumnClass('no-print')
        ];
    }

    private enrichContent(elements: DataImport[]): any {
        elements.forEach(el => {
            el['printedCreationDate'] = this.datetimeFormatterPipe.transform(el.creationTimestamp, 'YYYY-MM-DD HH:mm', this.timezone, this.language);
            el['showDownload'] = el.state == DataImportState.COMPLETED ? true : null;
            el['printedState'] = this.printState(el);
            el['showUnrecognizedColumns'] = el.unrecognizedColumns?.length > 0;
            el['unrecognizedColumnsTooltip'] = this.localizationPipe.transform('Ignored columns') + ': ' + el.unrecognizedColumns?.join(',');
        });
        return elements;
    }

    private printState(dataImport: DataImport): string {
        if (dataImport.state == DataImportState.PENDING && dataImport.totalCount != null && dataImport.importedCount != null) {
            const ofTranslation = this.localizationPipe.transform("of");
            const suffix = this.localizationPipe.transform("rows processed");
            let timeRemaining = "";
            if (dataImport.remainingSeconds != null) {
                const prefix = " (" + this.localizationPipe.transform("remaining time") + ": ";
                const time = this.durationFormatterPipe.transform({ startTimestamp: 0, endTimestamp: dataImport.remainingSeconds * 1000 });
                timeRemaining = prefix + time + ")";
            }
            return dataImport.importedCount + " " + ofTranslation + " " + dataImport.totalCount + " " + suffix + timeRemaining
        } else {
            return dataImport.state;
        }
    }

    openAddDialog(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.autoFocus = false;
        dialogConfig.viewContainerRef = this.vcRef;
        dialogConfig.data = { reconcileConnectionStatus: this.reconcileConnectionStatus }
        this.dialog.open(DataImportAddDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.dataImportWidgetService.showSnackbar("dataImportScheduledProperty");
                this.loadData();
                this.computeAddButtonDisabled();
            }
        });
    }

    execButtonAction(actionValue: ButtonActionValue): void {
        switch (actionValue.action) {
            case 'downloadDataImport':
                this.downloadFile(this.importData[actionValue.index]);
                break;
        }
    }

    private downloadFile(dataImport: DataImport) {
        this.stopRefreshing();
        const dialogRef = this.openDownloadDialog();
        this.dataImportWidgetService.getDataImportById(dataImport.id).then(res => {
            this.dataImportWidgetService.downloadFromExternalUrl(dataImport.name, res.url, res.dateTime);
            dialogRef.close();
            this.loadData();
            this.computeAddButtonDisabled();
            this.startRefreshing();
        }).catch(err => {
            this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
            dialogRef.close();
        });
    }

    private openDownloadDialog(): any {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '10%';
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = "download-progress-bar-dialog";
        dialogConfig.data = { title: "downloadsTitle" };
        return this.dialog.open(DownloadProgressBarDialogComponent, dialogConfig);
    }

}